import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material';
import {ChoseLocationComponent} from '../chose-location/chose-location.component';
import {CountryModel} from '../../models/country.model';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {Subscription} from 'rxjs';
import {AuthService} from '../../services/auth.service';
import {FilterHeaderService} from 'src/app/utils/services/filter-header.service';
import {TableLayoutService} from '../../services/table-layout.service';
import {Router} from '@angular/router';

@Component({
  selector: 'app-dynamic-filter',
  templateUrl: './dynamic-filter.component.html',
  styleUrls: ['./dynamic-filter.component.css']
})
export class DynamicFilterComponent implements OnInit, OnDestroy {
  @ViewChild('selectFilterView', {static: true}) selectFilterView;

  @Input() filterEntries: Array<any> = [];
  @Input() resetFilterEvent: EventEmitter<any>;
  @Input() loadFiltersEvent: EventEmitter<any>;
  @Input() filterLabel: string;

  @Input()
  set displayableFilters(value: Array<any>) {
    this._displayableFilters = value;
    this.initFilterForm();
  }

  @Output() filterChange = new EventEmitter<any>();
  @Output() actualFilterChange = new EventEmitter<string>();
  @Output() filterNoneSelected = new EventEmitter<Array<string>>();

  filterForm: FormGroup;
  selectedFiltersGroup: FormGroup;

  filterResetEventSubscription: Subscription;
  filterLoadEventSubscription: Subscription;
  _displayableFilters: Array<any> = [];


  constructor(public filterService: FilterHeaderService,
              private dialog: MatDialog,
              private fb: FormBuilder,
              private tableLayoutService: TableLayoutService,
              private router: Router,
              private authService: AuthService) {
    this.selectedFiltersGroup = this.fb.group({});
    this.filterForm = this.fb.group({
      selectFilter: [''],
      selectedFiltersGroup: this.selectedFiltersGroup
    });

    this.loadExistingFilterValues(filterService.selectedFilterTypes, filterService.selectedFilterEntries);
  }

  ngOnInit() {
    this.filterResetEventSubscription = this.resetFilterEvent.subscribe(() => {
      const filterTypes: [] = JSON.parse(JSON.stringify(this.filterService.selectedFilterTypes));

      this.removeUnnecessaryFilterControls(filterTypes);

      filterTypes.forEach(value => {
        this.removeSelectedFilter(value);
      });

      this.filterService.selectedFilterEntries = {};
      this.filterLabel = '';
      this.filterForm.updateValueAndValidity();
    });

    this.filterLoadEventSubscription = this.loadFiltersEvent.subscribe((filterData) => {
      const filterTypes = filterData['selectedFilterTypes'];
      const filterEntries = filterData['selectedFilterEntries'];

      this.loadExistingFilterValues(filterTypes, filterEntries);
      this.removeAddedFilters(filterTypes);

      this.filterService.selectedFilterTypes = filterTypes;
      this.filterService.selectedFilterEntries = filterEntries;
    });
  }

  private removeUnnecessaryFilterControls(filterTypes: []) {
    Object.keys(this.selectedFiltersGroup.controls)
      .forEach(key => {
        const foundFilter = filterTypes.find(r => r['key'] === key);

        if (!foundFilter) {
          this.selectedFiltersGroup.removeControl(key);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.filterResetEventSubscription) {
      this.filterResetEventSubscription.unsubscribe();
    }

    if (this.filterLoadEventSubscription) {
      this.filterLoadEventSubscription.unsubscribe();
    }
  }

  private initFilterForm() {
    this.applyDevSettings();

    this.selectedFiltersGroup.valueChanges.subscribe((value => {
      const filterValues = {};

      const noneSelectedFilter = [];

      Object.keys(value).forEach(key => {
        const filterEntry = value[key];

        if (filterEntry) {
          if (filterEntry.length > 0) {
            filterValues[key] = value[key];
          } else {
            noneSelectedFilter.push(key);
          }
        }
      });

      this.filterService.selectedFilterEntries = filterValues;
      this.filterChange.next(filterValues);

      if (noneSelectedFilter.length > 0) {
        this.filterNoneSelected.emit(noneSelectedFilter);
      }

      this.tableLayoutService.saveFilterSettingsToServer(this.router.url, {
        selectedFilterTypes: this.filterService.selectedFilterTypes,
        selectedFilterEntries: this.filterService.selectedFilterEntries
      });
    }));
  }

  private applyDevSettings() {
    if (this.authService.isTestUser === undefined) {
      this.authService.isTestingUser().subscribe(isTestUser => {
        if (!isTestUser) {
          this.removeDevColumns();
        }
      });
    } else if (!this.authService.isTestUser) {
      this.removeDevColumns();
    }
  }

  private removeDevColumns() {
    const skuId = this._displayableFilters.find(col => col.key === 'SKU_id');
    if (skuId) {
      this._displayableFilters.splice(this._displayableFilters.indexOf(skuId), 1);
    }

    const locProductCd = this._displayableFilters.find(col => col.key === 'Loc_Product_cd');
    if (locProductCd) {
      this._displayableFilters.splice(this._displayableFilters.indexOf(locProductCd), 1);
    }
  }

  private loadExistingFilterValues(filterTypes, filterEntries) {
    filterTypes.forEach(entry => {

      const filterEntry = filterEntries[entry['key']];

      const formControl = new FormControl(filterEntry);
      formControl.valueChanges.subscribe((value: Array<string>) => {
        this.actualFilterChange.emit(entry['key']);
      });

      this.selectedFiltersGroup.addControl(entry['key'], formControl);
      formControl.setValue(filterEntry);
    });

    this.filterChange.next(filterEntries);
  }

  private removeAddedFilters(filterTypes: Array<any>) {
    filterTypes.forEach(entry => {
      const displayableFilter = this._displayableFilters.find(x => x.key === entry.key);
      if (displayableFilter) {
        this._displayableFilters.splice(this._displayableFilters.indexOf(displayableFilter), 1);
      }
    });
  }

  addFilterType($event: any) {
    const filterValue = $event.value;

    const formControl = new FormControl(undefined);
    formControl.valueChanges.subscribe((value: Array<string>) => {
      this.actualFilterChange.emit(filterValue.key);
    });
    this.selectedFiltersGroup.addControl(filterValue.key, formControl);

    this.filterService.selectedFilterTypes.push(filterValue);
    this._displayableFilters.splice(this._displayableFilters.indexOf(filterValue), 1);
    this.selectFilterView.value = undefined;
    this.filterForm.controls['selectFilter'].setValue(undefined);
  }

  removeSelectedFilter(filter: any) {
    delete this.filterService.selectedFilterEntries[filter.key];
    this.selectedFiltersGroup.removeControl(filter.key);

    this.filterService.selectedFilterTypes.splice(this.filterService.selectedFilterTypes.indexOf(filter), 1);
    this._displayableFilters.push(filter);
    this._displayableFilters.sort((a, b) => a['position'] - b['position']);
    this.filterForm.controls['selectFilter'].setValue(undefined);
    this.selectedFiltersGroup.updateValueAndValidity();
  }

  removeFilterEntry(entry, filterKey) {
    this.filterService.selectedFilterEntries[filterKey].splice(this.filterService.selectedFilterEntries[filterKey].indexOf(entry), 1);
    this.selectedFiltersGroup.controls[filterKey].setValue(this.filterService.selectedFilterEntries[filterKey]);
  }

  openLocationDialog() {
    const dialogRef = this.dialog.open(ChoseLocationComponent, {
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((countries: Array<CountryModel>) => {
      if (countries !== undefined) {
        const countriesString = countries.map(country => country.name);

        this.selectedFiltersGroup.controls['Country_name'].setValue(countriesString);
        this.filterService.selectedFilterEntries['Country_name'] = countriesString;
      }
    });
  }

  deselectAll(filterKey: any) {
    this.filterService.selectedFilterEntries[filterKey] = [];
    this.selectedFiltersGroup.controls[filterKey].setValue([]);
  }

  selectAll(filterKey: any) {
    this.filterService.selectedFilterEntries[filterKey] = this.filterEntries[filterKey];
    this.selectedFiltersGroup.controls[filterKey].setValue(this.filterEntries[filterKey]);
  }
}
